import type { Alert24Variant } from '@/components/Alert24CTA';

/**
 * Alert24 funnel registry — Bucket 1 "SaaS funnel" pages.
 *
 * Blog posts listed here replace the consulting ContextualServiceCTA with an
 * Alert24 product CTA (alert24.net) in the blog sidebar. A page is either a
 * funnel page or an ads page (consumer-pages.ts), never both.
 *
 * Tools wire Alert24CTA directly inside their ToolClient instead (see
 * certificate-transparency-lookup, mtbf-mttr-calculator).
 */
export const ALERT24_BLOG_FUNNEL: Record<string, Alert24Variant> = {
  // Webhook guides → on-call/incident alerting (~430/mo each, GA4 trailing 28d)
  'jira-webhooks-guide': 'incident',
  'discord-webhooks-guide': 'incident',

  // Log management/observability guides → agent-side log monitoring
  // (low traffic individually, but perfect intent match — readers are actively
  // managing/troubleshooting log files)
  'complete-guide-to-log-files-types-examples-and-how-to-check-them': 'log-search',
  'log-aggregation-structured-parsing': 'log-search',
  'devops-log-analysis-infrastructure-troubleshooting': 'log-search',

  // Grok pattern cluster (June 2026) → agent-side log monitoring. Readers are
  // parsing/structuring log lines = exactly who Alert24 log monitoring serves.
  'grok-vs-regex': 'log-search',
  'grok-pattern-examples-common-logs': 'log-search',
  'how-to-debug-grok-patterns': 'log-search',
};

export function getAlert24VariantForBlog(slug: string): Alert24Variant | undefined {
  return ALERT24_BLOG_FUNNEL[slug];
}

/**
 * KB articles that carry an Alert24 CTA in the sidebar instead of the
 * consulting CTA. Keyed by `${category}/${slug}` (same convention as
 * GLITCHREPLAY_KB_FUNNEL / CONSUMER_KB_ARTICLES).
 */
export const ALERT24_KB_FUNNEL: Record<string, Alert24Variant> = {
  // Check Point "how to view logs" (~305 pv/mo) — best log-intent page on the
  // site; readers are firewall admins trying to inspect/search logs.
  'checkpoint/how-to-view-logs-and-reports': 'log-search',

  // "Where are X logs stored" series (devops category, June 2026) — mirrors the
  // proven where-configuration-files-are-stored KB pattern. Readers are actively
  // hunting log files = exactly who Alert24 agent-side log monitoring serves.
  'devops/where-are-nginx-logs-stored': 'log-search',
  'devops/where-are-apache-logs-stored': 'log-search',
  'devops/where-are-docker-container-logs-stored': 'log-search',
  'devops/where-are-postgresql-logs-stored': 'log-search',
  'devops/where-are-mysql-logs-stored': 'log-search',
  'devops/where-are-linux-system-logs-stored': 'log-search',
  'devops/where-are-systemd-journald-logs-stored': 'log-search',
  'devops/where-are-cron-logs-stored': 'log-search',
  'devops/where-are-pm2-logs-stored': 'log-search',
  'devops/where-are-iis-logs-stored': 'log-search',
};

export function getAlert24VariantForKB(category: string, slug: string): Alert24Variant | undefined {
  return ALERT24_KB_FUNNEL[`${category}/${slug}`];
}
